import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminNewsletter = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dialog, setDialog] = useState({ isOpen: false, type: 'alert', title: '', message: '', onConfirm: null });
  const navigate = useNavigate();

  const fetchSubscribers = async (page = 1) => {
    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter?page=${page}&limit=20`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        showAlert('Session Expired', 'Your session has expired. Please log in again.', () => navigate('/admin/login'));
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch subscribers');

      const data = await response.json();
      setSubscribers(data.items || []);
      setTotalPages(data.totalPages || 1);
      setCurrentPage(data.page || 1);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      showAlert('Error', 'Failed to load subscribers. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers(currentPage);
  }, [currentPage]);

  const showAlert = (title, message, onConfirm = null) => {
    setDialog({ isOpen: true, type: 'alert', title, message, onConfirm });
  };

  const showConfirm = (title, message, onConfirm) => {
    setDialog({ isOpen: true, type: 'confirm', title, message, onConfirm });
  };

  const closeDialog = () => {
    setDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleDelete = (subscriberId) => {
    showConfirm(
      'Delete Subscriber',
      'Are you sure you want to delete this subscriber? This action cannot be undone.',
      () => executeDelete(subscriberId)
    );
  };

  const executeDelete = async (subscriberId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/${subscriberId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to delete subscriber');

      showAlert('Success', 'Subscriber has been successfully deleted!');
      fetchSubscribers(currentPage);
    } catch (error) {
      console.error('Error deleting subscriber:', error);
      showAlert('Error', 'Failed to delete subscriber. Please try again.');
    }
  };

  const handleExportCSV = () => {
    if (subscribers.length === 0) {
      showAlert('Info', 'No subscribers available to export.');
      return;
    }
    
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Name,Email,Date Subscribed,Source\n"
      + subscribers.map(e => `${e.name || ''},${e.email},${new Date(e.createdAt).toLocaleDateString()},${e.source || 'homepage'}`).join("\n");
      
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `unframed-talks-subscribers-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link); // Required for FF
    link.click();
    document.body.removeChild(link);
  };

  const filteredSubscribers = subscribers.filter(sub => 
    sub.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.source?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-clash text-3xl font-bold text-electric-yellow">Newsletter Subscribers</h2>
        <button
          onClick={handleExportCSV}
          className="bg-electric-yellow text-midnight px-6 py-3 rounded-xl font-bold hover:bg-electric-yellow/90 transition-colors duration-300 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Export CSV
        </button>
      </div>

      {/* Search */}
      <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by email or source..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-midnight/80 border border-slate-dark rounded-xl pl-12 pr-4 py-3 text-warm-white placeholder-soft-gray focus:outline-none focus:ring-2 focus:ring-electric-yellow/50"
          />
          <svg 
            className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-soft-gray"
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Subscribers Table */}
      <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-dark/50 text-soft-gray text-sm">
              <tr>
                <th className="px-6 py-4 font-medium">Name</th>
                <th className="px-6 py-4 font-medium">Email</th>
                <th className="px-6 py-4 font-medium">Date Subscribed</th>
                <th className="px-6 py-4 font-medium">Source</th>
                <th className="px-6 py-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-dark/30">
              {loading ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-soft-gray">
                    <div className="animate-pulse flex items-center justify-center gap-2">
                      <div className="w-2 h-2 bg-electric-yellow rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-electric-yellow rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 bg-electric-yellow rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </td>
                </tr>
              ) : filteredSubscribers.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-soft-gray">
                    {searchTerm ? 'No subscribers found matching your search.' : 'No subscribers available.'}
                  </td>
                </tr>
              ) : (
                filteredSubscribers.map((sub) => (
                  <tr key={sub.id} className="hover:bg-slate-dark/20 transition-colors duration-200">
                    <td className="px-6 py-4">
                      <div className="font-medium text-warm-white">{sub.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-soft-gray">{sub.email}</div>
                    </td>
                    <td className="px-6 py-4 text-soft-gray">
                      {new Date(sub.createdAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-deep-teal/20 text-deep-teal border border-deep-teal/20 capitalize">
                        {sub.source || 'Homepage'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-3">
                      <button
                        onClick={() => handleDelete(sub.id)}
                        className="text-soft-gray hover:text-red-500 transition-colors p-2 rounded-lg hover:bg-red-500/10"
                        title="Delete subscriber"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-slate-dark/30 flex items-center justify-between">
            <span className="text-soft-gray text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-midnight/60 rounded-lg text-sm font-medium text-warm-white hover:bg-slate-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-slate-dark/50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-midnight/60 rounded-lg text-sm font-medium text-warm-white hover:bg-slate-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-slate-dark/50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Custom Dialog Modal */}
      {dialog.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-midnight/80 backdrop-blur-sm">
          <div className="bg-slate-dark border border-electric-yellow/30 rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
            <h3 className="text-xl font-bold text-electric-yellow mb-2">{dialog.title}</h3>
            <p className="text-soft-gray mb-6">{dialog.message}</p>
            <div className="flex justify-end gap-3">
              {dialog.type === 'confirm' && (
                <button
                  onClick={closeDialog}
                  className="px-4 py-2 rounded-lg font-medium text-soft-gray hover:text-white hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => {
                  if (dialog.onConfirm) dialog.onConfirm();
                  closeDialog();
                }}
                className={`px-4 py-2 rounded-lg font-bold transition-transform hover:scale-105 ${
                  dialog.type === 'confirm' 
                    ? 'bg-red-500 text-white shadow-lg shadow-red-500/20' 
                    : 'bg-electric-yellow text-midnight shadow-lg shadow-electric-yellow/20'
                }`}
              >
                {dialog.type === 'confirm' ? 'Confirm' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNewsletter;
